import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Spinner, InputGroup } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { fetchSongModels } from '../../redux/actions/songModelActions';

const CategoryForm = ({ 
  show, 
  onHide, 
  onSubmit, 
  editingCategory = null,
  loading = false 
}) => {
  const dispatch = useDispatch();
  const { items: songModels = [], loading: loadingSongModels } = useSelector((state) => ({
    items: state.songModels?.items || [],
    loading: state.songModels?.loading || false
  }));

  const [formData, setFormData] = useState({
    category_name: '',
    category_description: '',
    category_thumbnail: null
  });
  const [selectedSongModels, setSelectedSongModels] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [previewImage, setPreviewImage] = useState(null);
  const [error, setError] = useState('');

  // Fetch song models on mount
  useEffect(() => {
    if (show) {
      dispatch(fetchSongModels(1, 100, {}));
    }
  }, [dispatch, show]);


  useEffect(() => {
    if (editingCategory) {
      setFormData({
        category_name: editingCategory.category_name || '',
        category_description: editingCategory.category_description || '',
        category_thumbnail: null
      });
      // Set preview image if editing and category has thumbnail
      if (editingCategory.category_thumbnail) {
        setPreviewImage(editingCategory.category_thumbnail);
      }
      // Initialize selected song models if editing
      if (editingCategory.models && Array.isArray(editingCategory.models)) {
        const modelIds = editingCategory.models.map(model => model.id);
        setSelectedSongModels(modelIds);
      } else if (editingCategory.model_ids && Array.isArray(editingCategory.model_ids)) {
        setSelectedSongModels(editingCategory.model_ids);
      } else {
        setSelectedSongModels([]);
      }
    } else {
      setFormData({
        category_name: '',
        category_description: '',
        category_thumbnail: null
      });
      setPreviewImage(null);
      setSelectedSongModels([]);
    }
  }, [editingCategory, show]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSongModelChange = (e) => {
    const modelId = parseInt(e.target.value);
    const isChecked = e.target.checked;
    
    if (isChecked) {
      setSelectedSongModels(prev => [...prev, modelId]);
    } else {
      setSelectedSongModels(prev => prev.filter(id => id !== modelId));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        category_thumbnail: file
      }));
      
      // Create preview for the selected image
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    
    // Frontend validation
    if (!formData.category_name.trim()) {
      setError('Category name is required');
      return;
    }
    
    if (formData.category_name.length > 255) {
      setError('Category name must be less than 255 characters');
      return;
    }
    
    if (formData.category_description && formData.category_description.length > 500) {
      setError('Category description must be less than 500 characters');
      return;
    }
    
    if (formData.category_thumbnail) {
      const fileSize = formData.category_thumbnail.size / 1024 / 1024; // Convert to MB
      if (fileSize > 2) {
        setError('Thumbnail file size must be less than 2MB');
        return;
      }
      
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'];
      if (!allowedTypes.includes(formData.category_thumbnail.type)) {
        setError('Thumbnail must be a valid image file (JPEG, PNG, JPG, GIF)');
        return;
      }
    }
    
    // Create FormData for file upload
    const submitData = new FormData();
    
    if (editingCategory) {
      // For update, include the category ID
      submitData.append('id', editingCategory.id);
    }
    
    submitData.append('category_name', formData.category_name.trim());
    submitData.append('category_description', formData.category_description || '');
    
    if (formData.category_thumbnail) {
      submitData.append('category_thumbnail', formData.category_thumbnail);
    }

    // Add song model IDs in the specified format
    selectedSongModels.forEach((modelId, index) => {
      submitData.append(`model_ids[${index}]`, modelId);
    });

    onSubmit(submitData);
  };

  const handleClose = () => {
    setFormData({
      category_name: '',
      category_description: '',
      category_thumbnail: null
    });
    setSelectedSongModels([]);
    setSearchQuery('');
    setPreviewImage(null);
    setError('');
    onHide();
  };

  // Filter song models based on search query
  const filteredSongModels = songModels.filter(model =>
    model.model_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Modal show={show} onHide={handleClose} size="lg" scrollable fullscreen="md-down">
      <Modal.Header closeButton>
        <Modal.Title>
          {editingCategory ? 'Edit Category' : 'Add Category'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <div className="alert alert-danger mb-3">
            {error}
          </div>
        )}
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Category Name</Form.Label>
            <Form.Control
              type="text"
              name="category_name"
              value={formData.category_name}
              onChange={handleInputChange}
              required
              placeholder="Enter category name"
              maxLength={255}
            />
            <Form.Text className="text-muted">
              {formData.category_name.length}/255 characters
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              name="category_description"
              value={formData.category_description}
              onChange={handleInputChange}
              placeholder="Enter category description"
              maxLength={500}
            />
            <Form.Text className="text-muted">
              {formData.category_description.length}/500 characters
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Song Models</Form.Label>
            <InputGroup className="mb-2">
              <Form.Control
                type="text"
                placeholder="Search song models..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchQuery && (
                <Button 
                  variant="outline-secondary" 
                  onClick={() => setSearchQuery('')}
                >
                  Clear
                </Button>
              )}
            </InputGroup>
            {loadingSongModels ? (
              <div className="text-center">
                <Spinner animation="border" size="sm" />
                <span className="ms-2">Loading song models...</span>
              </div>
            ) : (
              <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ced4da', borderRadius: '0.375rem', padding: '10px' }}>
                {filteredSongModels.length === 0 ? (
                  <div className="text-muted">
                    {searchQuery ? 'No song models found matching your search' : 'No song models available'}
                  </div>
                ) : (
                  filteredSongModels.map((model) => (
                    <Form.Check
                      key={model.id}
                      type="checkbox"
                      id={`song-model-${model.id}`}
                      label={model.model_name}
                      value={model.id}
                      checked={selectedSongModels.includes(model.id)}
                      onChange={handleSongModelChange}
                      className="mb-2"
                    />
                  ))
                )}
              </div>
            )}
            <Form.Text className="text-muted">
              Select one or more song models to assign to this category
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Thumbnail</Form.Label>
            <Form.Control
              type="file"
              name="category_thumbnail"
              accept="image/*"
              onChange={handleFileChange}
              className="mb-2"
            />
            {previewImage && (
              <div className="mt-2">
                <img 
                  src={previewImage} 
                  alt="Preview" 
                  className="img-thumbnail" 
                  style={{ maxWidth: '200px', maxHeight: '200px' }}
                />
              </div>
            )}
            <Form.Text className="text-muted">
              Upload an image for the category thumbnail (JPEG, PNG, JPG, GIF, max 2MB)
            </Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? 'Saving...' : (editingCategory ? 'Update' : 'Add')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CategoryForm; 